import HmacSHA256 from 'crypto-js/hmac-sha256';
import Hex from 'crypto-js/enc-hex';
import {
  Method,
  promiseFetch,
  PromiseFetchSettings,
} from '@accedo/promise-fetch';
import { compile } from 'path-to-regexp';
import { getInternalErrorMessage } from '##/components/Error/error';
import { RequestRejectionError } from '##/services/tenApi/types';
import { config } from '##/services/store/store';
import { getAccessToken, logout } from '../authentication/Authentication';

const { apiBaseUrl, apiVersionTag, apiKey } = config.$get().api;

export const getBaseApiEndpoint = (endpoint = ''): string => {
  const fullUrl = `${apiBaseUrl}/api/${apiVersionTag}/`;

  return endpoint?.replace(fullUrl, '');
};

export const constructApiUrl = (
  methodPath: string,
  pathParams: Record<string, string | number> = {},
  queryParams: Record<string, string | number> = {},
): string => {
  const compiledMethodPath = compile(methodPath, {
    encode: encodeURIComponent,
  })(pathParams as Record<string, string | string[] | undefined>);

  const processedQueryParams = Object.keys(queryParams).map(
    (key) => `${key}=${queryParams[key]}`,
  );

  const fullUrl = `${apiBaseUrl}/api/${apiVersionTag}/${compiledMethodPath}`;

  return `${fullUrl}?${processedQueryParams.join('&')}`;
};

export const getVideoProgressPollUrl = (): string =>
  `${apiBaseUrl}/api/video-progress`;

export const getShowEndpoint = (showId: string): string =>
  `${apiBaseUrl}/api/${apiVersionTag}/shows/${showId}`;

export const sendApiRequest = async <T = never>(
  url: string,
  method: Method = 'GET',
  header: object = {},
  postData?: object,
): Promise<T> => {
  const newUrl = url.includes('?')
    ? `${url}&app=uaplite`
    : `${url}?app=uaplite`;
  const key = Hex.parse(apiKey);
  const timestamp = Date.now().toString();
  const signature = Hex.stringify(HmacSHA256(`${timestamp}:${url}`, key));
  const signatureText = `${timestamp}_${signature}`;

  const token = getAccessToken();
  let tokenHeader = {};
  if (token) {
    tokenHeader = {
      Authorization: `Bearer ${token}`,
    };
  }

  const headers = {
    'X-N10-SIG': signatureText,
    'Content-Type': 'application/json',
    'TP-AcceptFeature': 'v2/Live;',
    'TP-Platform': 'UAP',
  };

  const options: Partial<PromiseFetchSettings> = {
    method,
    responseType: 'json',
    headers: { ...headers, ...tokenHeader, ...header },
    data: JSON.stringify(postData),
  };

  return promiseFetch(newUrl, options)
    .then((response) => response.data)
    .catch((error) => {
      const internalErrorType = getInternalErrorMessage(error).type;
      const reason: RequestRejectionError = { internalErrorType, error };

      if (error?.status === 401) {
        logout();

        return undefined;
      }

      return Promise.reject(reason);
    });
};

export const sendApiRequestWithFwResponse = async <T = undefined> (
  url: string,
  method: Method = 'GET',
  header: object = {},
  isFreeWheelEnabled: boolean
): Promise<T> => {
  const newUrl = url.includes('?')
    ? `${url}&app=uaplite`
    : `${url}?app=uaplite`;
  const key = Hex.parse(apiKey);
  const timestamp = Date.now().toString();
  const signature = Hex.stringify(HmacSHA256(`${timestamp}:${url}`, key));
  const signatureText = `${timestamp}_${signature}`;

  const token = getAccessToken();
  let tokenHeader = {};
  if (token) {
    tokenHeader = {
      Authorization: `Bearer ${token}`,
    };
  }

  const headers = {
    'X-N10-SIG': signatureText,
    'Content-Type': 'application/json',
    'TP-AcceptFeature': 'v2/Live;',
    'TP-Platform': 'UAP',
  };

  const options: Partial<PromiseFetchSettings> = {
    method,
    responseType: 'json',
    headers: { ...headers, ...tokenHeader, ...header },
  };

  return promiseFetch(newUrl, options)
    .then((response) => ({ data: response.data, authToken: isFreeWheelEnabled ? response.xhr.getResponseHeader('x-dai-auth') : '' }) as unknown as T)
    .catch((error) => {
      const internalErrorType = getInternalErrorMessage(error).type;
      const reason: RequestRejectionError = { internalErrorType, error };

      if (error?.status === 401) {
        logout();

        return undefined as never;
      }

      return Promise.reject(reason);
    });
};
